/**
 * DistributionChart — ECharts bar chart for queue time label distribution.
 * Shows Walk On / Short / Moderate / Long as percentage bars.
 */
import React from 'react';
import ReactECharts from 'echarts-for-react';
import 'echarts/lib/chart/bar';
import 'echarts/lib/component/tooltip';
import 'echarts/lib/component/title';

export interface DistributionChartProps {
  data: { walkOn: number; shortWait: number; moderateWait: number; longWait: number };
  thresholds?: { walkOn: number; shortWait: number; moderateWait: number; longWait: number };
  isLoading?: boolean;
  attractionName?: string;
}

const BAR_COLORS = ['#10B981', '#22D3EE', '#F59E0B', '#EF4444'];

function toPercent(num: number, total: number): number {
  if (total === 0) return 0;
  return Math.round((num / total) * 1000) / 10;
}

export function DistributionChart({
  data,
  isLoading = false,
  attractionName,
}: DistributionChartProps): React.ReactElement {
  const total = data.walkOn + data.shortWait + data.moderateWait + data.longWait;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <span className="text-gray-400 dark:text-gray-500">Loading...</span>
      </div>
    );
  }

  if (total === 0) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <span className="text-gray-400 dark:text-gray-500">No queue time data for this attraction in the selected range</span>
      </div>
    );
  }

  const values = [data.walkOn, data.shortWait, data.moderateWait, data.longWait];
  const percentages = values.map((v) => toPercent(v, total));
  const labels = ['Walk On', 'Short', 'Moderate', 'Long'];

  const option: any = {
    title: {
      text: attractionName ?? '',
      left: 'center',
      top: 4,
      textStyle: { fontSize: 14, fontWeight: 600, color: '#374151' },
    },
    grid: { left: 40, right: 16, top: 56, bottom: 32, containLabel: false },
    xAxis: {
      type: 'category',
      data: labels,
      axisLabel: { show: false },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      show: false,
    },
    series: [
      {
        type: 'bar',
        data: values.map((v, i) => ({
          value: percentages[i],
          raw: v,
          itemStyle: { color: BAR_COLORS[i] },
        })),
        barWidth: '50%',
        label: {
          show: true,
          position: 'top',
          formatter: (params: any) =>
            `${params.data?.value ?? 0}%`,
          fontSize: 12,
          fontWeight: 600,
          color: '#374151',
        },
        emphasis: {
          itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,0.15)' },
        },
      },
    ],
    tooltip: {
      trigger: 'item' as const,
      formatter: (params: any) => {
        const count = params.data.raw;
        const idx = labels.indexOf(params.name);
        const pct = idx >= 0 ? toPercent(values[idx], total) : 0;
        return `${params.name}<br/>Count: ${count}<br/>Percentage: ${pct}%`;
      },
    },
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg shadow p-4">
      <ReactECharts option={option} style={{ height: 280 }} notMerge />
    </div>
  );
}
